import { GenericAbortSignal } from 'axios';

import http from 'services/http';

import { RetrievalMode } from './clientPortalSystems';
import { PortalScriptPurpose } from './portalScripts';

// The end user's read-only view of their client's Connected Portals (OLIO-3018): one row per
// configured (client, source system), carrying the retrieval mode, the headless credential
// mirror, what has been recorded per purpose, the portal's scan history, and the readiness
// verdict. The server owns every one of those derivations — this side renders them.
//
// Read-only by design. Credentials are still saved through portalCredentials.ts and revoking
// one stays admin-only (clientPortalSystems.ts).

// A named cause, not a boolean — the reason a portal isn't ready, rendered as copy. snake_case
// inside a camelCase envelope, the same convention workflow value names follow: these are
// value names, not field keys, so OliveBranch leaves them alone.
export type PortalConnectionBlocker =
  | 'no_credentials'
  | 'credentials_invalid'
  | 'no_retrieval_script'
  | 'no_discovery_script'
  | 'manual_mode'
  | 'no_coordinating_group';

// Mirrored back from the integration platform, which owns the credential. Null outside headless
// mode — extension mode keeps credentials in the browser and manual mode has none — so this is
// absent rather than a row of nulls the UI has to know to ignore. status 'active' with a null
// verifiedAt means "saved, never confirmed".
export interface PortalConnectionCredentialsOptions {
  status: 'active' | 'invalid' | null;
  username: string | null;
  savedAt: string | null;
  verifiedAt: string | null;
}

// `owned` says whether THE script that would be replayed is the caller's own or one the client's
// sharing level lets them borrow. Only an owned script can be archived, so re-record affordances
// key off it rather than off `recorded`.
export interface PortalConnectionRecordingOptions {
  recorded: boolean;
  recordedAt: string | null;
  owned: boolean;
}

export interface PortalConnectionOptions {
  sourceSystem: string;
  // The portal's name as the server spells it. THE label for this source system — the front end
  // no longer keeps its own map.
  displayName: string;
  retrievalMode: RetrievalMode;
  credentials: PortalConnectionCredentialsOptions | null;
  recordings: Record<PortalScriptPurpose, PortalConnectionRecordingOptions>;
  lastScanAt: string | null;
  liveScanWorkflowId: string | null;
  // ready === blockers.empty?, derived server-side where retrieval_mode decides which axes
  // apply. Never recomputed here — a UI that says READY while dispatch quietly falls back to
  // the attended flow is exactly what one shared derivation exists to prevent.
  ready: boolean;
  blockers: PortalConnectionBlocker[];
  // Scanning is the narrower verdict: it needs the DISCOVERY recording and ignores the retrieval
  // one, so a portal can be scannable while not fully ready, and vice versa.
  canScan: boolean;
  scanBlockers: PortalConnectionBlocker[];
}

// No parameters: the rows are derived per acting client, so there is one list per session and
// `list` takes no arguments — unlike the admin settings key, which is per client id.
export const portalConnectionQueryKeys = {
  base: ['portalConnections'] as const,
  list: () => ['portalConnections', 'list'] as const,
};

export async function indexPortalConnections(signal?: GenericAbortSignal) {
  return http.get<{ data: PortalConnectionOptions[] }>('portal_connections', { signal }).then((res) => res.data.data);
}
